import { io } from 'socket.io-client';
import soundService from './soundService';

// Server URL priority:
// 1. Environment variable (for custom deployments)
// 2. Railway URL (primary production)
// 3. Localhost (development)
const getServerUrl = () => {
  if (process.env.REACT_APP_SERVER_URL) {
    return process.env.REACT_APP_SERVER_URL;
  }
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:3001';
  }
  // Production: Use Railway
  return 'https://kartenspiel-server-production.up.railway.app';
};

const SERVER_URL = getServerUrl();

class SocketService {
  constructor() {
    this.socket = null;
    this.pendingListeners = []; // Queue for listeners before socket exists
    this.currentRoom = null;  // Store current room info for rejoin
    this.currentNickname = null;
    this.setupVisibilityHandler();
  }

  // Handle tab visibility changes (mobile tab switching, screen lock, etc.)
  setupVisibilityHandler() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.socket) {
          // Small delay to let browser stabilize after tab switch
          setTimeout(() => {
            if (this.socket && !this.socket.connected) {
              console.log('Tab visible - reconnecting...');
              this.socket.connect();
            }
          }, 500);
        }
      });
    }
  }

  /**
   * Helper: Emit event and return Promise
   * Handles timeout, success event, and error event cleanup automatically
   *
   * @param {string} emitEvent - Event name to emit
   * @param {object} data - Data to send
   * @param {string} successEvent - Event name for successful response
   * @param {string} errorMessage - Default error message
   * @param {number} timeout - Timeout in ms (default: 5000)
   * @param {string} errorEvent - Custom error event name (default: 'error')
   * @returns {Promise} - Resolves with response data or rejects with error
   */
  emitWithPromise(emitEvent, data, successEvent, errorMessage = 'Fehler', timeout = 5000, errorEvent = 'error') {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.socket.off(successEvent);
        this.socket.off(errorEvent);
        reject(new Error('Timeout'));
      }, timeout);

      this.socket.once(errorEvent, (err) => {
        clearTimeout(timeoutId);
        this.socket.off(successEvent);
        reject(new Error(err.message || errorMessage));
      });

      this.socket.emit(emitEvent, data);

      this.socket.once(successEvent, (responseData) => {
        clearTimeout(timeoutId);
        this.socket.off(errorEvent);
        resolve(responseData);
      });
    });
  }

  connect() {
    // Only create a new connection if not already connected
    if (this.socket && this.socket.connected) {
      return this.socket;
    }

    // If socket exists but is disconnected, reconnect it
    if (this.socket) {
      this.socket.connect();
      return this.socket;
    }

    // Create new socket connection with robust reconnection options
    this.socket = io(SERVER_URL, {
      reconnection: true,
      reconnectionAttempts: Infinity,  // Keep trying forever
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      // Prevent connection drops during idle
      pingTimeout: 60000,
      pingInterval: 25000,
    });

    this.socket.on('connect', () => {
      console.log('Connected to server:', this.socket.id);
      // Register any pending listeners
      this.processPendingListeners();

      // Auto-rejoin room if we were in one (but only if not on lobby page)
      if (this.currentRoom && this.currentNickname && !this.skipAutoRejoin) {
        console.log('Auto-rejoining room:', this.currentRoom);
        this.socket.emit('rejoinRoom', {
          roomId: this.currentRoom,
          nickname: this.currentNickname,
        });
      }
    });

    // Handle rejoin failure - clear stored room
    this.socket.on('rejoinFailed', (data) => {
      console.error('Rejoin failed:', data?.reason || 'unknown', data?.message || '');
      console.log('Was trying to rejoin room:', this.currentRoom, 'as:', this.currentNickname);
      this.currentRoom = null;
      this.currentNickname = null;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from server:', reason);
      // If server closed connection, try to reconnect manually
      if (reason === 'transport close' || reason === 'ping timeout') {
        console.log('Connection lost - will auto-reconnect...');
        soundService.play('connection_lost');
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('Reconnected after', attemptNumber, 'attempts');
      soundService.play('connection_restored');
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('Reconnection attempt', attemptNumber);
    });

    this.socket.on('reconnect_error', (error) => {
      console.log('Reconnection error:', error.message);
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    // Clear room info on intentional disconnect
    this.currentRoom = null;
    this.currentNickname = null;

  }

  getSocket() {
    return this.socket;
  }

  // Clear stored room info (call when entering lobby)
  clearStoredRoom() {
    this.currentRoom = null;
    this.currentNickname = null;

  }

  // Process queued listeners after socket connects
  processPendingListeners() {
    if (!this.socket) return;
    while (this.pendingListeners.length > 0) {
      const { event, callback } = this.pendingListeners.shift();
      this.socket.on(event, callback);
    }
  }

  // Register listener (queues if socket not ready)
  registerListener(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
    } else {
      this.pendingListeners.push({ event, callback });
    }
  }

  // Generic emit for dev tools and other uses
  emit(event, data) {
    if (this.socket) {
      this.socket.emit(event, data);
    } else {
      console.warn(`[SocketService] Cannot emit '${event}' - socket not connected`);
    }
  }

  // Room management
  // userId: Optional Dynamic.xyz User ID for stats tracking
  createRoom(nickname, cardsPerPlayer, isLocalTestMode = false, localPlayerCount = 4, gameMode = null, gameModeTarget = null, turnTimeLimit = null, userId = null) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.socket.off('roomCreated');
        this.socket.off('error');
        reject(new Error('Timeout beim Erstellen des Raums'));
      }, 5000);

      this.socket.once('error', (err) => {
        clearTimeout(timeout);
        this.socket.off('roomCreated');
        reject(new Error(err.message || 'Fehler beim Erstellen des Raums'));
      });

      this.socket.emit('createRoom', {
        nickname,
        cardsPerPlayer,
        isLocalTestMode,
        localPlayerCount,
        gameMode,
        gameModeTarget,
        turnTimeLimit,
        userId, // Pass userId to server for stats tracking
      });

      this.socket.once('roomCreated', (data) => {
        clearTimeout(timeout);
        this.socket.off('error');
        // Store room info for auto-rejoin
        this.currentRoom = data.roomId;
        this.currentNickname = nickname;

        resolve(data);
      });
    });
  }

  // userId: Optional Dynamic.xyz User ID for stats tracking
  joinRoom(roomId, nickname, userId = null) {
    return new Promise((resolve, reject) => {
      this.socket.emit('joinRoom', { roomId, nickname, userId });

      const timeout = setTimeout(() => {
        this.socket.off('roomJoined');
        this.socket.off('error');
        reject(new Error('Timeout'));
      }, 5000);

      this.socket.once('roomJoined', (data) => {
        clearTimeout(timeout);
        this.socket.off('error');
        // Store room info for auto-rejoin
        this.currentRoom = roomId;
        this.currentNickname = nickname;
        resolve(data);
      });

      this.socket.once('error', (error) => {
        clearTimeout(timeout);
        this.socket.off('roomJoined');
        reject(error);
      });
    });
  }

  getRooms() {
    this.socket.emit('getRooms');
  }

  leaveRoom() {
    this.socket.emit('leaveRoom');
    // Clear room info
    this.currentRoom = null;
    this.currentNickname = null;

  }

  /**
   * Signalisiert dem Server, dass das Spiel beendet ist und wir zur Lobby zurückkehren
   * (Stammtisch: Befördert wartende Mitglieder zu Spielern)
   */
  returnToLobbyAfterGame() {
    this.socket.emit('returnToLobbyAfterGame');
  }

  /**
   * Startet ein neues Spiel im selben Raum (nach Victory)
   * Server setzt GameState zurück und wechselt zur Lobby/Stammtisch-Phase
   */
  newGame() {
    this.socket.emit('game:action', { action: 'newGame' });
  }

  // ============================================
  // PRIVATE ROOM METHODS
  // ============================================

  /**
   * Erstellt einen neuen privaten Raum (Membership-basiert)
   * @param {string} name - Raumname (wird zu Slug konvertiert)
   * @param {string} nickname - Nickname des Erstellers
   * @param {string} userId - Dynamic.xyz User ID
   * @param {object} config - Raum-Konfiguration (gameMode, gameModeTarget, etc.)
   */
  createPrivateRoom(name, nickname, userId, config = {}) {
    return this.emitWithPromise(
      'createPrivateRoom',
      { name, nickname, userId, ...config },
      'privateRoomCreated',
      'Fehler beim Erstellen des privaten Raums',
      10000
    ).then(data => {
      this.currentRoom = data.roomId;
      this.currentNickname = nickname;

      return data;
    });
  }

  /**
   * Tritt einem privaten Raum bei (Membership-basiert)
   * @param {string} slug - Raum-Slug
   * @param {string} nickname - Nickname
   * @param {string} userId - Dynamic.xyz User ID (Pflicht)
   */
  joinPrivateRoom(slug, nickname, userId) {
    return this.emitWithPromise(
      'joinPrivateRoom',
      { slug, nickname, userId },
      'privateRoomJoined',
      'Fehler beim Beitreten',
      10000
    ).then(data => {
      this.currentRoom = data.roomId;
      this.currentNickname = nickname;

      return data;
    });
  }

  /**
   * Verlässt einen privaten Raum (nur für Members, nicht Owner)
   */
  leavePrivateRoom(slug, userId) {
    return this.emitWithPromise(
      'leavePrivateRoom',
      { slug, userId },
      'leftRoom',
      'Fehler beim Verlassen'
    );
  }

  /**
   * Kickt einen Member aus dem Raum (nur Owner)
   * @param {boolean} ban - Optional: auch bannen
   */
  kickMember(slug, userId, targetUserId, ban = false) {
    return this.emitWithPromise(
      'kickMember',
      { slug, userId, targetUserId, ban },
      'memberKicked',
      'Fehler beim Entfernen'
    );
  }

  /**
   * Aktualisiert Raum-Einstellungen (nur Owner)
   */
  updateRoomSettings(slug, userId, settings) {
    return this.emitWithPromise(
      'updateRoomSettings',
      { slug, userId, settings },
      'roomSettingsUpdated',
      'Fehler beim Aktualisieren'
    );
  }

  /**
   * Aktualisiert den Raum-Namen (nur Owner)
   */
  updateRoomName(slug, userId, displayName) {
    return this.emitWithPromise(
      'updateRoomName',
      { slug, userId, displayName },
      'roomNameUpdated',
      'Fehler beim Umbenennen'
    );
  }

  /**
   * Archiviert oder reaktiviert einen Raum (nur Owner)
   */
  archiveRoom(slug, userId, archived = true) {
    return this.emitWithPromise(
      'archiveRoom',
      { slug, userId, archived },
      'roomArchived',
      'Fehler beim Archivieren'
    );
  }

  /**
   * Überträgt Ownership an ein anderes Mitglied (nur Owner)
   */
  transferOwnership(slug, userId, targetUserId) {
    return this.emitWithPromise(
      'transferOwnership',
      { slug, userId, targetUserId },
      'ownershipTransferred',
      'Fehler beim Übertragen'
    );
  }

  /**
   * Löscht einen Raum permanent (nur Owner, mit Name-Bestätigung)
   */
  permanentlyDeleteRoom(slug, userId, confirmName) {
    return this.emitWithPromise(
      'permanentlyDeleteRoom',
      { slug, userId, confirmName },
      'roomPermanentlyDeleted',
      'Fehler beim Löschen',
      10000
    );
  }

  /**
   * Generiert einen neuen Invite-Token (nur Owner)
   */
  regenerateInviteToken(slug, userId) {
    return this.emitWithPromise(
      'regenerateInviteToken',
      { slug, userId },
      'inviteTokenRegenerated',
      'Fehler beim Erneuern des Links'
    );
  }

  /**
   * Bannt ein Mitglied (nur Owner)
   */
  banMember(slug, userId, targetUserId) {
    return this.emitWithPromise(
      'banMember',
      { slug, userId, targetUserId },
      'memberBanned',
      'Fehler beim Bannen'
    );
  }

  /**
   * Entbannt ein Mitglied (nur Owner)
   */
  unbanMember(slug, userId, targetUserId) {
    return this.emitWithPromise(
      'unbanMember',
      { slug, userId, targetUserId },
      'memberUnbanned',
      'Fehler beim Entbannen'
    );
  }

  /**
   * Holt die Ban-Liste eines Raums (nur Owner)
   */
  getRoomBans(slug, userId) {
    return this.emitWithPromise(
      'getRoomBans',
      { slug, userId },
      'roomBans',
      'Fehler beim Laden der Ban-Liste'
    ).then(data => data.bans || []);
  }

  /**
   * Holt alle Members eines Raums
   */
  getRoomMembers(slug, userId) {
    return this.emitWithPromise(
      'getRoomMembers',
      { slug, userId },
      'roomMembers',
      'Fehler beim Laden der Mitglieder'
    ).then(data => data.members || []);
  }

  /**
   * Holt alle privaten Räume des Users
   */
  getMyRooms(userId) {
    return this.emitWithPromise(
      'getMyRooms',
      { userId },
      'myRooms',
      'Fehler beim Laden der Räume'
    ).then(data => data.rooms || []);
  }

  /**
   * Stellt einen privaten Raum wieder her (nur für Owner)
   */
  restorePrivateRoom(slug, nickname, userId) {
    return this.emitWithPromise(
      'restorePrivateRoom',
      { slug, nickname, userId },
      'privateRoomRestored',
      'Fehler beim Wiederherstellen',
      10000
    ).then(data => {
      this.currentRoom = data.roomId;
      this.currentNickname = nickname;

      return data;
    });
  }

  /**
   * Holt Stats & History für einen privaten Raum
   * Spieler muss bereits im Raum sein
   */
  getRoomStats(slug) {
    return this.emitWithPromise(
      'getRoomStats',
      { slug },
      'roomStats',
      'Fehler beim Laden der Stats',
      10000,
      'roomStatsError'
    );
  }

  startGame(options = {}) {
    this.socket.emit('startGame', options);
  }

  updateGameMode(gameMode, gameModeTarget) {
    this.socket.emit('updateGameMode', { gameMode, gameModeTarget });
  }

  updateModePresets(modePresets) {
    this.socket.emit('updateModePresets', { modePresets });
  }

  updateCardsPerPlayer(cardsPerPlayer) {
    this.socket.emit('updateCardsPerPlayer', { cardsPerPlayer });
  }

  updateLocalPlayerCount(localPlayerCount) {
    this.socket.emit('updateLocalPlayerCount', { localPlayerCount });
  }

  updateTurnTimeLimit(turnTimeLimit) {
    this.socket.emit('updateTurnTimeLimit', { turnTimeLimit });
  }

  updateMagicCardDrawChance(drawChance) {
    // drawChance: null = use default from Balancing Editor, number = override (0-1)
    this.socket.emit('updateMagicCardDrawChance', { drawChance });
  }

  updateSealDrawChance(drawChance) {
    // drawChance: null = use default (2%), number = override (0-1)
    this.socket.emit('updateSealDrawChance', { drawChance });
  }

  updateBettingInterval(interval) {
    // interval: null = use default (3), 0 = disabled, 2-10 = custom
    this.socket.emit('updateBettingInterval', { interval });
  }

  placeBet(target, stake) {
    this.socket.emit('bettingPlaceBet', { target, stake });
  }

  playerAction(action, cards = null, forPlayerIndex = null) {
    this.socket.emit('playerAction', { action, cards, forPlayerIndex });
  }

  // Joker actions
  buyJoker(jokerId, forPlayerIndex = null) {
    this.socket.emit('buyJoker', { jokerId, forPlayerIndex });
  }

  sellJoker(jokerId, forPlayerIndex = null) {
    this.socket.emit('sellJoker', { jokerId, forPlayerIndex });
  }

  // Shop actions
  buyBooster(boosterIndex, selectedSealId) {
    this.socket.emit('buyBooster', { boosterIndex, selectedSealId });
  }

  // Buy seal item (single seal or booster)
  buySealItem(itemIndex, selectedSealId = null, forPlayerIndex = null) {
    this.socket.emit('buySealItem', { itemIndex, selectedSealId, forPlayerIndex });
  }

  // Buy seal bundle (V3.0) - selectedSeals is array of seal objects
  buyBundle(selectedSeals, forPlayerIndex = null) {
    this.socket.emit('buyBundle', { selectedSeals, forPlayerIndex });
  }

  // Buy joker bundle (V3.0) - selectedJokerId is the ID of the chosen joker
  buyJokerBundle(selectedJokerId, forPlayerIndex = null) {
    this.socket.emit('buyJokerBundle', { selectedJokerId, forPlayerIndex });
  }

  // Sell seal from stash (V3.0)
  sellSeal(sealId, slotIndex, forPlayerIndex = null) {
    this.socket.emit('sellSeal', { sealId, slotIndex, forPlayerIndex });
  }

  // Reroll shop items (costs: 2, 4, 6 coins)
  rerollShopItems(forPlayerIndex = null) {
    this.socket.emit('rerollShopItems', { forPlayerIndex });
  }

  // Reroll shop jokers (costs: 2, 4, 6 coins)
  rerollJokers(forPlayerIndex = null) {
    this.socket.emit('rerollJokers', { forPlayerIndex });
  }

  equipSeal(sealId) {
    this.socket.emit('equipSeal', { sealId });
  }

  // Shield pass action (use shield to pass instead of drawing)
  shieldPass(forPlayerIndex = null) {
    this.socket.emit('playerAction', { action: 'shieldPass', forPlayerIndex });
  }

  // Peek at draw pile (Sneak Peak seal ability)
  peekDrawPile(forPlayerIndex = null) {
    this.socket.emit('peekDrawPile', { forPlayerIndex });
  }

  // Apply seal ring to selected cards
  applySealRing(selectedCards, forPlayerIndex = null) {
    this.socket.emit('applySealRing', { selectedCards, forPlayerIndex });
  }

  // Use magnet to draw highest card from deck
  useMagnet(forPlayerIndex = null) {
    this.socket.emit('useMagnet', { forPlayerIndex });
  }

  // Use swap to trade a hand card for a random deck card
  useSwap(card, forPlayerIndex = null) {
    this.socket.emit('useSwap', { card, forPlayerIndex });
  }

  // Use Glücksgriff to apply random seal from stash to selected card
  useGluecksgriff(card, forPlayerIndex = null) {
    this.socket.emit('useGluecksgriff', { card, forPlayerIndex });
  }

  shopReady() {
    this.socket.emit('shopReady');
  }

  // Exchange actions
  exchangeCards(cards, forPlayerIndex = null) {
    this.socket.emit('exchangeCards', { cards, forPlayerIndex });
  }

  // Set cards per player for next round (only Asso can do this)
  // When confirm=true, cards will be dealt
  setCardsPerPlayer(cardsPerPlayer, confirm = false, forPlayerIndex = null) {
    this.socket.emit('setCardsPerPlayer', { cardsPerPlayer, confirm, forPlayerIndex });
  }

  // Proceed from round end to shop
  proceedToShop() {
    this.socket.emit('proceedToShop');
  }

  // Proceed from round end to victory (when game is complete)
  proceedToVictory() {
    this.socket.emit('game:action', { action: 'proceedToVictory' });
  }

  // Submit vote for playing again (victory phase)
  submitVote(vote) {
    this.socket.emit('game:action', { action: 'submitVote', vote });
  }

  // Event listeners (queued if socket not ready)
  onRoomListUpdate(callback) {
    this.registerListener('roomListUpdate', callback);
  }

  onPlayerJoined(callback) {
    this.registerListener('playerJoined', callback);
  }

  onPlayerLeft(callback) {
    this.registerListener('playerLeft', callback);
  }

  onGameStarted(callback) {
    this.registerListener('gameStarted', callback);
  }

  onGameStateUpdate(callback) {
    this.registerListener('gameStateUpdate', callback);
  }

  onActionReceived(callback) {
    this.registerListener('actionReceived', callback);
  }

  onGameEnded(callback) {
    this.registerListener('gameEnded', callback);
  }

  onNewHost(callback) {
    this.registerListener('newHost', callback);
  }

  onError(callback) {
    this.registerListener('error', callback);
  }

  onPeekResult(callback) {
    this.registerListener('peekResult', callback);
  }

  onSealRingResult(callback) {
    this.registerListener('sealRingResult', callback);
  }

  onMagnetResult(callback) {
    this.registerListener('magnetResult', callback);
  }

  onSwapResult(callback) {
    this.registerListener('swapResult', callback);
  }

  onGluecksgriffResult(callback) {
    this.registerListener('gluecksgriffResult', callback);
  }

  onShopPhaseStarted(callback) {
    this.registerListener('shopPhaseStarted', callback);
  }

  onBoosterPurchased(callback) {
    this.registerListener('boosterPurchased', callback);
  }

  onSealPurchased(callback) {
    this.registerListener('sealPurchased', callback);
  }

  onBoosterRevealed(callback) {
    this.registerListener('boosterRevealed', callback);
  }

  onShopPlayerChanged(callback) {
    this.registerListener('shopPlayerChanged', callback);
  }

  onPlayerShopReady(callback) {
    this.registerListener('playerShopReady', callback);
  }

  onExchangePhaseStarted(callback) {
    this.registerListener('exchangePhaseStarted', callback);
  }

  onPlayerExchangeReady(callback) {
    this.registerListener('playerExchangeReady', callback);
  }

  onCardsPerPlayerChanged(callback) {
    this.registerListener('cardsPerPlayerChanged', callback);
  }

  onCardsDealtForExchange(callback) {
    this.registerListener('cardsDealtForExchange', callback);
  }

  onExchangePlayerChanged(callback) {
    this.registerListener('exchangePlayerChanged', callback);
  }

  onRoundEnded(callback) {
    this.registerListener('roundEnded', callback);
  }

  onRoundEnding(callback) {
    this.registerListener('roundEnding', callback);
  }

  onJokerPurchased(callback) {
    this.registerListener('jokerPurchased', callback);
  }

  onBundlePurchased(callback) {
    this.registerListener('bundlePurchased', callback);
  }

  onJokerSold(callback) {
    this.registerListener('jokerSold', callback);
  }

  onJokerBundlePurchased(callback) {
    this.registerListener('jokerBundlePurchased', callback);
  }

  onJokerEffect(callback) {
    this.registerListener('jokerEffect', callback);
  }

  onScoreUpdate(callback) {
    this.registerListener('scoreUpdate', callback);
  }

  onPlayerFinished(callback) {
    this.registerListener('playerFinished', callback);
  }

  onCardAnimation(callback) {
    this.registerListener('cardAnimation', callback);
  }

  onWheelSpin(callback) {
    this.registerListener('wheelSpin', callback);
  }

  onWheelSpinResult(callback) {
    this.registerListener('wheelSpinResult', callback);
  }

  // Badge System
  emitPlayerBadgesRequest(userId) {
    if (this.socket && userId) {
      this.socket.emit('getPlayerBadges', { userId });
    }
  }

  // Remove listeners
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
    // Also remove from pending listeners
    this.pendingListeners = this.pendingListeners.filter(
      l => !(l.event === event && (callback === undefined || l.callback === callback))
    );
  }
}

const socketService = new SocketService();

// Dev: expose for console testing
if (typeof window !== 'undefined') {
  window.socketService = socketService;
}

export default socketService;
